/**
 * Helpers para transformar o recorte devolvido pelo react-easy-crop
 * (em pixels da imagem original) num File pronto para upload.
 */

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', reject)
        image.setAttribute('crossOrigin', 'anonymous')
        image.src = src
    })
}

function toRadians(degrees) {
    return (degrees * Math.PI) / 180
}

function buildFileName(originalName, extension) {
    const base = (originalName || 'imagem').replace(/\.[^./\\]+$/, '')
    return `${base}-recortada.${extension}`
}

/**
 * @param {string} imageSrc         object URL da imagem original
 * @param {object} cropPixels       { x, y, width, height } em pixels da imagem original
 * @param {number} rotation         graus (0, 90, 180, 270)
 * @param {string} fileName         nome do arquivo original
 * @param {string} mimeType         mime do arquivo original
 * @param {number} maxWidth         largura máxima do resultado (evita uploads gigantes)
 * @param {number} quality          qualidade do JPEG/WebP
 * @returns {Promise<File>}
 */
export async function getCroppedImageFile({
    imageSrc,
    cropPixels,
    rotation = 0,
    fileName,
    mimeType,
    maxWidth = 1600,
    quality = 0.92,
}) {
    const image = await loadImage(imageSrc)

    // PNG preserva transparência; qualquer outro formato sai como JPEG.
    const outputType = mimeType === 'image/png' ? 'image/png' : 'image/jpeg'
    const extension = outputType === 'image/png' ? 'png' : 'jpg'

    // 1. Desenha a imagem já rotacionada numa canvas auxiliar.
    const radians = toRadians(rotation)
    const rotatedWidth =
        Math.abs(Math.cos(radians) * image.width) + Math.abs(Math.sin(radians) * image.height)
    const rotatedHeight =
        Math.abs(Math.sin(radians) * image.width) + Math.abs(Math.cos(radians) * image.height)

    const rotatedCanvas = document.createElement('canvas')
    rotatedCanvas.width = Math.round(rotatedWidth)
    rotatedCanvas.height = Math.round(rotatedHeight)
    const rotatedCtx = rotatedCanvas.getContext('2d')

    rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2)
    rotatedCtx.rotate(radians)
    rotatedCtx.drawImage(image, -image.width / 2, -image.height / 2)

    // 2. Recorta a área escolhida e reescala se passar do limite.
    const scale = Math.min(1, maxWidth / cropPixels.width)
    const outputCanvas = document.createElement('canvas')
    outputCanvas.width = Math.round(cropPixels.width * scale)
    outputCanvas.height = Math.round(cropPixels.height * scale)
    const outputCtx = outputCanvas.getContext('2d')

    if (outputType === 'image/jpeg') {
        outputCtx.fillStyle = '#FFFFFF'
        outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height)
    }

    outputCtx.imageSmoothingEnabled = true
    outputCtx.imageSmoothingQuality = 'high'
    outputCtx.drawImage(
        rotatedCanvas,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        outputCanvas.width,
        outputCanvas.height
    )

    const blob = await new Promise((resolve, reject) => {
        outputCanvas.toBlob(
            (result) => (result ? resolve(result) : reject(new Error('canvas_to_blob_failed'))),
            outputType,
            quality
        )
    })

    return new File([blob], buildFileName(fileName, extension), {
        type: outputType,
        lastModified: Date.now(),
    })
}

export default getCroppedImageFile
